import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from '../i18n/translations';
import { listCached, getCached, cachedGet } from '../lib/apiCache';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Look up product slug for the target language given a slug currently in the URL.
// Returns the alternate slug if known, otherwise the original.
const resolveProductSlugForLanguage = (currentSlug, targetLang) => {
  if (!currentSlug) return currentSlug;

  // Try a fast direct cache hit first
  let product =
    getCached(`/api/products/${currentSlug}`) ||
    getCached(`/api/products/${encodeURIComponent(currentSlug)}`);

  // Fallback: scan all cached entries for a product matching by slug/slugRu/id
  if (!product) {
    for (const [key, data] of listCached()) {
      if (!data) continue;
      // Only scan product endpoints
      if (!key.includes('/api/products')) continue;

      // Lists of products (may be under items key)
      const list =
        Array.isArray(data) ? data :
        Array.isArray(data?.items) ? data.items :
        null;

      if (list) {
        const hit = list.find(
          (p) =>
            p &&
            (p.slug === currentSlug ||
              p.slugRu === currentSlug ||
              p.id === currentSlug)
        );
        if (hit) {
          product = hit;
          break;
        }
      } else if (typeof data === 'object') {
        if (
          data.slug === currentSlug ||
          data.slugRu === currentSlug ||
          data.id === currentSlug
        ) {
          product = data;
          break;
        }
      }
    }
  }

  if (!product) return currentSlug;

  if (targetLang === 'ru') {
    return product.slugRu || product.slug || product.id || currentSlug;
  }
  return product.slug || product.id || currentSlug;
};

// Look up the category matching the current URL slug and return the alternate
// slug for the target language. Uses cached /api/categories list when available.
const resolveCategorySlugForLanguage = (currentSlug, targetLang) => {
  if (!currentSlug) return currentSlug;

  const categories = getCached('/api/categories');
  if (!Array.isArray(categories)) return currentSlug;

  const found = categories.find(
    (c) =>
      c &&
      (c.slug === currentSlug || c.slugRu === currentSlug || c.id === currentSlug)
  );
  if (!found) return currentSlug;

  if (targetLang === 'ru') {
    return found.slugRu || found.slug || found.id || currentSlug;
  }
  return found.slug || found.id || currentSlug;
};

// Rewrite the slug segment of a language-aware path according to the target
// language. Supports /product/<slug> and /category/<slug> (with optional /ru
// prefix). Returns the original path if it does not match any known pattern.
const rewriteLocalizedSlug = (pathname, targetLang) => {
  // Product routes
  const productMatch = pathname.match(/^(\/ru)?\/product\/([^/?#]+)(.*)$/);
  if (productMatch) {
    const [, ruPrefix, slug, rest] = productMatch;
    const desiredSlug = resolveProductSlugForLanguage(
      decodeURIComponent(slug),
      targetLang
    );
    if (desiredSlug === slug) return pathname;
    return `${ruPrefix || ''}/product/${encodeURIComponent(desiredSlug)}${rest}`;
  }

  // Category routes
  const categoryMatch = pathname.match(/^(\/ru)?\/category\/([^/?#]+)(.*)$/);
  if (categoryMatch) {
    const [, ruPrefix, slug, rest] = categoryMatch;
    const desiredSlug = resolveCategorySlugForLanguage(
      decodeURIComponent(slug),
      targetLang
    );
    if (desiredSlug === slug) return pathname;
    return `${ruPrefix || ''}/category/${encodeURIComponent(desiredSlug)}${rest}`;
  }

  return pathname;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [language, setLanguage] = useState(() => {
    // Check URL for /ru prefix first
    if (location.pathname.startsWith('/ru')) {
      return 'ru';
    }
    // Otherwise get from localStorage or default to 'ro'
    return localStorage.getItem('language') || 'ro';
  });

  const [categoriesPrimed, setCategoriesPrimed] = useState(!!getCached('/api/categories'));

  useEffect(() => {
    // Save language to localStorage whenever it changes
    localStorage.setItem('language', language);
  }, [language]);

  // Prime the categories cache once on mount so cold navigation to /category/<slug>
  // (with language=ru already in localStorage) can immediately rewrite the slug.
  useEffect(() => {
    if (categoriesPrimed) return;
    let cancelled = false;
    cachedGet('/api/categories')
      .then(() => {
        if (!cancelled) setCategoriesPrimed(true);
      })
      .catch(() => {
        if (!cancelled) setCategoriesPrimed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [categoriesPrimed]);

  // Sync URL with language (also rewrites /product/<slug> when needed so the
  // single navigate updates both the /ru prefix and the right slug atomically).
  useEffect(() => {
    const currentPath = location.pathname;

    // Admin panel is always accessible without /ru prefix — skip sync for these routes
    if (currentPath.startsWith('/admin')) {
      return;
    }

    // If someone lands on /ru/admin, strip the /ru prefix
    if (currentPath.startsWith('/ru/admin')) {
      navigate(currentPath.replace(/^\/ru/, ''), { replace: true });
      return;
    }

    // Step 1: rewrite the slug for the current path according to active language
    const slugRewritten = rewriteLocalizedSlug(currentPath, language);

    // Step 2: add or remove the /ru prefix as needed
    let nextPath = slugRewritten;
    if (language === 'ru' && !nextPath.startsWith('/ru')) {
      nextPath = '/ru' + nextPath;
    } else if (language === 'ro' && nextPath.startsWith('/ru')) {
      nextPath = nextPath.replace(/^\/ru/, '') || '/';
    }

    if (nextPath !== currentPath) {
      navigate(nextPath + location.search + location.hash, { replace: true });
    }
  }, [language, location.pathname, location.search, location.hash, navigate, categoriesPrimed]);

  const t = (key) => {
    // Navigate through nested keys (e.g., 'navbar.home')
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }

    return value || key;
  };

  const changeLanguage = (lang) => {
    if (lang === 'ro' || lang === 'ru') {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
